/**
 * BUG-0168：内容与任务 ↔ 发布管理 的上下文往返。
 *
 * 只接受白名单参数（content / package / status / q / from），并只允许在站内固定路由之间跳转，
 * 不接收任意外部 returnTo，避免开放重定向；篡改 from 会退回默认页面。
 */

export const PUBLICATION_CONTEXT_KEYS = ['content', 'package', 'status', 'q', 'from'] as const;
export type PublicationContextKey = (typeof PUBLICATION_CONTEXT_KEYS)[number];
export type PublicationSourcePage = 'content' | 'package';

/** 发布页只接受这两个来源页；其他值一律视为无来源。 */
export function parsePublicationSource(value: string | null | undefined): PublicationSourcePage | null {
  return value === 'content' || value === 'package' ? value : null;
}

/** 只保留白名单参数，丢弃外部注入的 returnTo/returnUrl 等键。 */
export function sanitizePublicationContext(params: URLSearchParams): Record<PublicationContextKey, string> {
  const context = { content: '', package: '', status: '', q: '', from: '' } as Record<PublicationContextKey, string>;
  for (const key of PUBLICATION_CONTEXT_KEYS) context[key] = params.get(key) ?? '';
  return context;
}

/** 内容与任务页 / 服务包页 → 发布管理「安排发布计划」。 */
export function publicationPlanPath(input: {
  projectId: string; contentId: string; packageId: string; status?: string | undefined; q?: string | undefined;
  from: PublicationSourcePage;
}): string {
  const query = new URLSearchParams();
  query.set('content', input.contentId);
  if (input.packageId) query.set('package', input.packageId);
  if (input.status) query.set('status', input.status);
  if (input.q) query.set('q', input.q);
  query.set('from', input.from);
  query.set('action', 'plan');
  return `/projects/${input.projectId}/publications?${query.toString()}`;
}

/**
 * 发布管理 → 回原页面。from=content 回内容与任务页（带筛选与搜索词），
 * from=package 回服务包页；两者都只拼站内固定路径，不接受外部地址。
 */
export function publicationReturnPath(input: {
  projectId: string; contentId: string; packageId: string; status?: string | undefined; q?: string | undefined;
  from: PublicationSourcePage; contentType?: 'VIDEO' | 'ARTICLE' | undefined;
}): string {
  if (input.from === 'package') {
    const query = new URLSearchParams();
    if (input.packageId) query.set('package', input.packageId);
    query.set('content', input.contentId);
    query.set('from', 'publications');
    if (input.contentType === 'ARTICLE') query.set('type', 'ARTICLE');
    return `/projects/${input.projectId}/packages?${query.toString()}`;
  }
  const query = new URLSearchParams();
  query.set('content', input.contentId);
  if (input.packageId) query.set('package', input.packageId);
  if (input.status) query.set('status', input.status);
  if (input.q) query.set('q', input.q);
  query.set('from', 'publications');
  return `/projects/${input.projectId}/content?${query.toString()}`;
}

/** 从发布页参数里还原回跳路径；来源缺失或 content 为空时返回 null（保持默认行为）。 */
export function resolvePublicationReturn(params: URLSearchParams, projectId: string,
  contentType?: 'VIDEO' | 'ARTICLE'): string | null {
  const from = parsePublicationSource(params.get('from'));
  const contentId = params.get('content');
  if (!from || !contentId) return null;
  return publicationReturnPath({ projectId, contentId, packageId: params.get('package') ?? '',
    status: params.get('status') ?? '', q: params.get('q') ?? '', from, contentType });
}
